/**
 * Dealer Health — M8 platform module.
 *
 * Weighted 0-100 composite health score with grade (A ≥85 … E), the
 * per-factor breakdown (weight × sub-score = contribution) and a 30-snapshot
 * history bar strip. Data: platformService.health / healthHistory /
 * recomputeHealth. MOCK_* fallbacks keep the page rendering without a
 * backend.
 */
import { useMemo } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { platformService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';

const gradeColor = (grade) =>
  grade === 'A' || grade === 'B' ? 'var(--success)'
    : grade === 'C' ? 'var(--warning)'
    : 'var(--danger)';

const FACTOR_META = {
  leadVolume:   { label: 'Lead volume (7d)',            detail: (f) => `${fmtRaw(f.leadsLast7d)} leads · full marks at ≥20` },
  firstContact: { label: 'First contact ≤15min (7d)',   detail: (f) => `${fmtRaw(f.within15MinPctLast7d)}% of leads` },
  showRate:     { label: 'Appointment show rate (30d)', detail: (f) => `${fmtRaw(f.showRatePctLast30d)}% shown` },
  activeUsers:  { label: 'Active users',                detail: (f) => `${fmtRaw(f.activeUserCount)} active · full marks at ≥5` },
};

function fmtRaw(v) {
  return v == null || Number(v) < 0 ? '—' : Number(v).toLocaleString('en-AU');
}
const fmtDate = (iso) => formatDateTime(iso);
const fmtPct  = (v)   => (v == null ? '—' : `${Number(v).toFixed(1)}%`);

/* ── Mock fallbacks ── */
const MOCK_HEALTH = {
  id: 'mock-h0', score: 76, grade: 'B', computedAt: '2026-07-14T02:00:00Z',
  factors: {
    leadVolume:   { weight: 0.25, score: 85, available: true, leadsLast7d: 17 },
    firstContact: { weight: 0.30, score: 68, available: true, within15MinPctLast7d: 68 },
    showRate:     { weight: 0.25, score: 72, available: true, showRatePctLast30d: 72 },
    activeUsers:  { weight: 0.20, score: 80, available: true, activeUserCount: 4 },
    availableWeight: 1,
  },
};
const MOCK_HISTORY = Array.from({ length: 30 }, (_, i) => {
  const score = Math.max(35, Math.min(95, Math.round(76 + Math.sin(i * 0.6) * 12 - i * 0.4)));
  const grade = score >= 85 ? 'A' : score >= 70 ? 'B' : score >= 55 ? 'C' : score >= 40 ? 'D' : 'E';
  return {
    id: `mock-h${i + 1}`, score, grade,
    computedAt: new Date(Date.now() - i * 86400000).toISOString(),
  };
});

export default function DealerHealth() {
  const { toast } = useToast();

  const { data: health, refetch: refetchHealth } = useApi(
    () => platformService.health(),
    { fallback: MOCK_HEALTH, deps: [] }
  );
  const { data: history, refetch: refetchHistory } = useApi(
    () => platformService.healthHistory(),
    { fallback: MOCK_HISTORY, deps: [] }
  );

  const recompute = useMutation(platformService.recomputeHealth);

  const onRecompute = async () => {
    try {
      const fresh = await recompute.mutate();
      toast(`Health score recomputed — ${fresh?.score ?? '?'} / 100 (${fresh?.grade ?? '?'})`, { variant: 'success' });
      refetchHealth();
      refetchHistory();
    } catch (err) {
      toast(`Recompute failed: ${err?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const factors = health?.factors || {};
  const factorRows = Object.keys(FACTOR_META)
    .filter((k) => factors[k])
    .map((k) => ({ key: k, ...FACTOR_META[k], ...factors[k] }));

  /* History arrives newest first — flip to chronological for the strip. */
  const historyRows = useMemo(
    () => [...(Array.isArray(history) ? history : [])].reverse().slice(-30),
    [history]
  );

  const color = gradeColor(health?.grade);

  return (
    <>
      <TopBar
        crumbs={['Dealer Health']}
        right={
          <button className="btn small primary" disabled={recompute.mutating} onClick={onRecompute}>
            {recompute.mutating ? 'Recomputing…' : '↻ Recompute'}
          </button>
        }
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Dealer Health</div>
            <div className="page-header-sub">Adoption, responsiveness and data quality — one weighted score</div>
          </div>
        </div>

        {/* ── Big score card ── */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'center' }}>
              <div className="metric-value" style={{ fontSize: 56, lineHeight: 1, color }}>
                {health?.score ?? '—'}
                <span style={{ fontSize: 20, color: 'var(--text-tertiary)' }}> /100</span>
              </div>
            </div>
            <div style={{
              width: 64, height: 64, borderRadius: '50%',
              border: `3px solid ${color}`, color,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 30, fontWeight: 800,
            }}>
              {health?.grade || '—'}
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div className="mono text-xs text-muted">LAST COMPUTED</div>
              <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{fmtDate(health?.computedAt)}</div>
              <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
                Grades: A ≥85 · B ≥70 · C ≥55 · D ≥40 · else E
              </div>
            </div>
          </div>
        </div>

        {/* ── Factor breakdown ── */}
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-label" style={{ marginBottom: 10 }}>FACTOR BREAKDOWN</div>
          {factorRows.length === 0 && (
            <div className="text-muted text-xs">No factor data on the latest snapshot.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {factorRows.map((f) => {
              const weightPct = (Number(f.weight) || 0) * 100;
              const subScore = f.available === false || f.score == null ? null : Number(f.score);
              const contribution = subScore == null ? 0 : (Number(f.weight) || 0) * subScore;
              return (
                <div key={f.key}>
                  <div className="flex-between" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      {f.label}
                      <span className="mono text-xs text-muted" style={{ marginLeft: 8 }}>
                        weight {fmtPct(weightPct)}
                      </span>
                    </span>
                    <span className="mono text-xs" style={{ fontWeight: 600 }}>
                      {subScore == null
                        ? <span style={{ color: 'var(--text-tertiary)' }}>unavailable</span>
                        : <>score {subScore} · contributes {contribution.toFixed(1)}</>}
                    </span>
                  </div>
                  <div style={{ background: 'var(--bg-glass)', borderRadius: 4, height: 12 }}>
                    <div style={{
                      width: `${Math.min(100, contribution)}%`, height: '100%',
                      borderRadius: 4,
                      background: subScore == null ? 'var(--text-tertiary)'
                        : subScore >= 70 ? 'var(--success)'
                        : subScore >= 50 ? 'var(--warning)' : 'var(--danger)',
                      minWidth: contribution > 0 ? 2 : 0,
                    }} />
                  </div>
                  <div className="mono text-xs text-muted" style={{ marginTop: 3 }}>
                    {f.detail(f)}
                  </div>
                </div>
              );
            })}
          </div>
          {factors.availableWeight != null && Number(factors.availableWeight) < 1 && (
            <div className="mono text-xs text-muted" style={{ marginTop: 10 }}>
              Only {fmtPct(Number(factors.availableWeight) * 100)} of the weighting was measurable —
              missing factors are excluded and the rest re-normalised.
            </div>
          )}
        </div>

        {/* ── History strip (last 30 snapshots) ── */}
        <div className="card">
          <div className="flex-between" style={{ marginBottom: 10 }}>
            <span className="card-label" style={{ margin: 0 }}>SCORE HISTORY</span>
            <span className="mono text-xs text-muted">last {historyRows.length} snapshots</span>
          </div>
          {historyRows.length === 0 ? (
            <div className="text-muted text-xs">No snapshots yet — hit Recompute to record the first one.</div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 90 }}>
                {historyRows.map((h) => (
                  <div
                    key={h.id}
                    title={`${h.score} / 100 (${h.grade}) · ${fmtDate(h.computedAt)}`}
                    style={{
                      flex: 1,
                      height: `${Math.max(4, h.score)}%`,
                      background: gradeColor(h.grade),
                      opacity: 0.85,
                      borderTopLeftRadius: 2, borderTopRightRadius: 2,
                    }}
                  />
                ))}
              </div>
              <div className="flex-between mono text-xs text-muted" style={{ marginTop: 6 }}>
                <span>{fmtDate(historyRows[0]?.computedAt)}</span>
                <span>{fmtDate(historyRows[historyRows.length - 1]?.computedAt)}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
